import { lazy, Suspense, useEffect } from 'react'
import { Thread } from '@assistant-ui/react-ui'
import { useThreadRuntime } from '@assistant-ui/react'
import '@assistant-ui/react-ui/styles/index.css'
import '@assistant-ui/react-ui/styles/markdown.css'
// Import AFTER assistant-ui styles so our overrides take precedence
import './chat-panel-theme.css'
import { MarkdownText } from '@/features/chat/components/markdown-text'
import { MessageReactions } from '@/features/chat/components/message-reactions'
import { BotRosterStrip } from '@/features/chat/components/bot-roster-strip'
import { studioBotApplicationId } from '@/features/chat/services/studio-agent-runtime-a2a'
import { PipelineStepper } from '@/features/chat/components/pipeline-stepper'
import { PresetSelector } from '@/features/design-system'
import { BlockDiffCard, useBlockDiff } from '@/features/chat/components/block-diff-card'
import { SuggestionChips } from '@/features/chat/components/suggestion-chips'
import { commandBus } from '@/features/chat'
import { IntentPreviewHost } from '@/features/agent/components/intent-preview-host'
import { useStudioStore } from '@/store/studio-store'
import { ChatMessages } from '@/shell/ide-v2/components/chat-messages'
import { StudioTaskCenter } from '@/shell/ide-v2/components/studio-task-center'
import { deriveStudioInteractionCapabilities } from '@/shell/ide-v2/studio-interaction-capabilities'
import { TemplateAuthoringPanel } from '@/features/templates/authoring/TemplateAuthoringPanel'
import { FolderOpen, X } from 'lucide-react'

const CHAT_NOTICE_AUTO_DISMISS_MS = 8000

const StudioComposer = lazy(() =>
  import('@/features/chat/components/studio-composer').then((module) => ({
    default: module.StudioComposer,
  })),
)

function BlockDiffSection() {
  const diff = useBlockDiff()
  if (!diff) return null

  return (
    <div className="px-4 pb-2">
      <BlockDiffCard
        blockName={diff.blockName}
        beforeSummary={diff.beforeSummary}
        afterSummary={diff.afterSummary}
        changedFields={diff.changedFields}
        applied={diff.applied}
        onApply={() => commandBus.dispatch({ type: 'applyBlockDiff', payload: {} })}
        onUndo={() => commandBus.dispatch({ type: 'undoBlockDiff', payload: {} })}
      />
    </div>
  )
}

/**
 * Show design system preset selector during active app creation phases.
 *
 * C5 — developer-only surface: the dropdown exposes a raw sentinel value
 * ("__default__") and developer-facing preset chrome. The creator persona
 * never sees it; the design system implicitly stays on the default and
 * remains reachable in developer mode via the PersonaSwitcher (same
 * precedent as Tasks 5/6 — see connected-document-panel.tsx,
 * report-workspace-surface.tsx).
 */
function DesignSystemSection() {
  const user = useStudioStore((s) => s.user)
  const phase = useStudioStore((s) => s.conversation.phase)
  const setConversation = useStudioStore((s) => s.setConversation)
  const studioPersona = useStudioStore((s) => s.studioPersona)

  const showPreset =
    studioPersona === 'developer' &&
    user &&
    ['gathering', 'scenarios', 'pages', 'page-ui', 'theming'].includes(phase)
  if (!showPreset) return null

  return (
    <div className="px-4 py-2 border-b border-border">
      <PresetSelector
        userId={user.id}
        onSelect={(preset) => setConversation({ designSystemPreset: preset })}
      />
    </div>
  )
}

function IntentPreviewSection() {
  return (
    <div className="px-4 pb-2">
      <IntentPreviewHost />
    </div>
  )
}

/**
 * The `pendingWorkChoice` surface — the answer to a question the client asked.
 *
 * Exported and mounted beside `ChatNoticeSection` for the same reason that one
 * is: a surface that renders on only one of the two chat shells is a surface
 * that silently does nothing half the time.
 *
 * Rows, not parsed prose. When the update-existing-work request is ambiguous,
 * the client asks which project — and before this existed, the user's typed
 * answer matched no detector and went to the model, which replied "let me load
 * the app" and called nothing (measured in production, 2026-08-04). Each row
 * carries the id the client already resolved, so picking is a click on
 * `openServerProject` — the same call the Context tab picker uses, which was
 * verified opening `/ide/<id>` in production the day before.
 *
 * IMPORT SURFACE IS DELIBERATELY EMPTY. The first version of this component
 * pulled `@/i18n` and `@/shell/ide-v2/coogo-ide-chrome-copy` into chat-panel
 * for one screen-reader label. That was the only thing in the change that
 * added cross-tree module edges, and the build carrying it stopped booting on
 * www.cogo.xyz while the identical bytes booted everywhere else — a cause
 * still not identified after thirteen refuted hypotheses. The label is gone
 * rather than re-imported: the row's accessible name is already its project
 * name and date, which is what the user is choosing between, and the
 * surrounding reply already says what the list is for. No new module edge is
 * worth re-opening that question.
 */
export function ChatWorkChoiceSection() {
  const choices = useStudioStore((s) => s.pendingWorkChoice)
  const setPendingWorkChoice = useStudioStore((s) => s.setPendingWorkChoice)
  const openServerProject = useStudioStore((s) => s.openServerProject)

  if (!choices || choices.length === 0) return null

  return (
    <div className="px-4 pt-1">
      <div data-testid="chat-work-choice" className="grid gap-2">
        {choices.map((choice) => (
          <button
            key={choice.id}
            type="button"
            data-testid="chat-work-choice-row"
            className="flex w-full items-center gap-2 rounded-md border border-border bg-card/80 px-3 py-2 text-left text-[12px] leading-snug text-foreground shadow-sm transition-colors hover:bg-background"
            onClick={() => {
              // Clear first: the open switches chat threads, and a list left
              // standing would re-offer a choice already made.
              setPendingWorkChoice(null)
              void openServerProject(choice.id)
            }}
          >
            <FolderOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate font-medium">{choice.name}</span>
            {choice.updatedOn ? (
              <span className="shrink-0 text-[11px] text-muted-foreground">{choice.updatedOn}</span>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * The `pendingChatNotice` surface.
 *
 * Exported because it must render on BOTH chat shells. `ChatRail` (ide-v2)
 * delegates to `ChatDrawerSlot` → this panel only in EDIT mode; in BUILD mode
 * it renders `BuildModeChat`, which had no notice section — so a notice set
 * while the workspace was in build mode was stored and never painted.
 *
 * That silently affected every producer on this channel, not one feature:
 * `surfaceStudioGenerationFailure` (the durable surface for a failed
 * generation, which exists precisely because a toast was too easy to miss)
 * and `openStudioLaunchChat` (the dashboard's own open-project copy).
 * Measured and locked by e2e/studio/chat-notice-build-mode.spec.ts.
 */
export function ChatNoticeSection() {
  const notice = useStudioStore((s) => s.pendingChatNotice)
  const setPendingChatNotice = useStudioStore((s) => s.setPendingChatNotice)

  useEffect(() => {
    if (!notice) return
    const timer = window.setTimeout(() => {
      if (useStudioStore.getState().pendingChatNotice === notice) {
        setPendingChatNotice(null)
      }
    }, CHAT_NOTICE_AUTO_DISMISS_MS)
    return () => window.clearTimeout(timer)
  }, [notice, setPendingChatNotice])

  if (!notice) return null

  return (
    <div className="px-4 pt-1">
      <div
        data-testid="chat-pending-notice"
        data-collaboration-role="cogo"
        className="flex items-start gap-2 rounded-md border border-border bg-card/80 px-3 py-2 text-[12px] leading-snug text-foreground shadow-sm"
      >
        <div className="min-w-0 flex-1">{notice}</div>
        <button
          type="button"
          aria-label="Dismiss"
          className="-mr-1 -mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
          onClick={() => setPendingChatNotice(null)}
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}

/** Auto-send pending chat message from Quick Create or other entry flows */
function usePendingChatMessage() {
  const runtime = useThreadRuntime()
  const pendingChatMessage = useStudioStore((s) => s.pendingChatMessage)

  useEffect(() => {
    if (!pendingChatMessage) return

    // Small delay to ensure Thread is mounted
    const timer = setTimeout(() => {
      runtime.append({
        role: 'user',
        content: [{ type: 'text', text: pendingChatMessage }],
      })
      useStudioStore.getState().setPendingChatMessage(null)
    }, 500)

    return () => clearTimeout(timer)
  }, [pendingChatMessage, runtime])
}

function useAgenticUxChatMounted() {
  const transaction = useStudioStore((s) => s.agenticUxTransaction)

  useEffect(() => {
    if (!transaction || transaction.status !== 'running') return
    if (transaction.phase !== 'route-committed' && transaction.phase !== 'project-opened') return
    useStudioStore.getState().advanceAgenticUxTransaction(transaction.id, {
      phase: 'chat-mounted',
    })
    const focus = () => {
      window.dispatchEvent(
        new CustomEvent('cogo:focus-chat-input', {
          detail: { transactionId: transaction.id },
        }),
      )
    }
    window.requestAnimationFrame?.(() => {
      focus()
      window.setTimeout(focus, 50)
      window.setTimeout(focus, 250)
    })
    window.setTimeout(focus, 500)
  }, [transaction])
}

function useWelcomeMessage(): string {
  const hasProject = useStudioStore((s) => Boolean(s.currentProject?.manifest?.name))
  return hasProject ? 'What would you like to change?' : 'What would you like to build?'
}

/** Empty composer placeholder — the real composer is rendered outside Thread */
function EmptyComposer() {
  return null
}

/**
 * Which bot this chat talks to, resolved the SAME way the send path resolves
 * it (assign-chat-to-agent-runtime): the project's own slug when it has a
 * publication, otherwise the appliance's live bot. Deriving it here from the
 * same inputs keeps the strip from advertising a bot the message would not
 * actually reach.
 */
function ChatBotRosterSection() {
  const slug = useStudioStore((s) => s.projectMeta?.slug ?? null)
  // Same derivation the send path uses -- deliberately the SAME function, not
  // an equivalent copy. When these two decided separately they disagreed and
  // the strip advertised a bot the message never reached.
  return <BotRosterStrip applicationId={studioBotApplicationId(slug)} />
}

function ChatTemplateAuthoringSection() {
  const currentProject = useStudioStore((s) => s.currentProject)
  const currentProjectId = useStudioStore((s) => s.currentProjectId)
  const projectMeta = useStudioStore((s) => s.projectMeta)
  const capabilities = deriveStudioInteractionCapabilities({
    currentProjectId,
    currentProject,
    projectMetadata:
      (projectMeta?.metadata as Record<string, unknown> | undefined) ?? null,
  })
  const hasAtLeastOneBlock = Object.keys(currentProject?.blocks ?? {}).length > 0

  if (capabilities.hasTemplateContent || !hasAtLeastOneBlock) return null
  return <TemplateAuthoringPanel key={currentProjectId ?? 'unsaved-studio-project'} />
}

export function ChatPanel() {
  usePendingChatMessage()
  useAgenticUxChatMounted()
  const welcomeMessage = useWelcomeMessage()

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto">
        {/* Assistant-ui owns rich message rendering, but it must not reserve
            fixed blank space when Studio system messages are the active thread. */}
        <div className="min-h-0 shrink-0">
          <Thread
            welcome={{ message: welcomeMessage }}
            assistantMessage={{
              allowCopy: false,
              allowReload: false,
              components: { Text: MarkdownText, Footer: MessageReactions },
            }}
            components={{ Composer: EmptyComposer }}
          />
        </div>
        <ChatMessages
          layout="inline"
          hideWhenEmpty
          progressOnly
          showActionFlowTrace
          workDetails={
            <>
              <PipelineStepper />
              <DesignSystemSection />
              <BlockDiffSection />
              <IntentPreviewSection />
              <StudioTaskCenter />
              <ChatBotRosterSection />
              <ChatTemplateAuthoringSection />
            </>
          }
          className="border-t border-border/60 bg-background"
        />
      </div>

      {/* Fixed bottom section — only transient notice + composer once an app is open. */}
      <div className="border-t border-border bg-background pb-3 pt-2 flex flex-col gap-2">
        <ChatNoticeSection />
        <ChatWorkChoiceSection />
        <SuggestionChips />
        <div className="px-4">
          <Suspense
            fallback={
              <div
                aria-hidden="true"
                className="h-[58px] rounded-md border border-border bg-muted/20"
                data-testid="studio-composer-loading"
              />
            }
          >
            <StudioComposer />
          </Suspense>
        </div>
      </div>
    </div>
  )
}
